import { Component, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
  IonContent,
  ModalController,
  Platform,
  ViewWillLeave,
} from '@ionic/angular';
import {
  PanAppState,
  PlatformService,
  UsabillaService,
} from '@panamax/app-state';
import { Product } from '@shared/models/product.model';
import { AlertIdData, Note, Notification } from '@usf/alert-types';
import { Customer } from '@usf/customer-types/customer';
import { Subscription, combineLatest, Subject, Observable } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  take,
  takeUntil,
  catchError,
  skip,
} from 'rxjs/operators';
import { CustomerStoreService } from 'src/app/ngrx-customer/services';
import { HelpModalDesktopComponent } from 'src/app/shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from 'src/app/shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { PATHS } from 'src/app/shared/constants/paths';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { environment } from '../../../environments/environment';
import { OrderService } from '../../order/services/order.service';
import { ModalWithNavComponent } from '../../shared/components/modal-with-nav/modal-with-nav.component';
import { ModalService } from '../../shared/services/modal/modal.service';
import { NotificationFiltersDesktopTabletComponent } from '../components/notification-filters/notification-filters-desktop-tablet/notification-filters-desktop-tablet.component';
import { NotificationFiltersMobileComponent } from '../components/notification-filters/notification-filters-mobile/notification-filters-mobile.component';
import { AlertTypeEnum } from '../constants/constants';
import { NotificationFilters } from '../model/notification-filters.model';
import { NotificationsService } from '../services/notifications.service';
import { UpcService } from '@shared/services/upc/upc.service';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { ALERTS_ACTIONS, NOTES_ACTIONS } from '@usf/ngrx-alerts';
import { UserService } from '@app/user/services';
import { FEATURES } from '@shared/constants/splitio-features';
import { LoginService } from '@app/login/services';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { of } from 'rxjs';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.page.html',
  styleUrls: ['./notifications.page.scss'],
})
export class NotificationsPage implements ViewWillLeave, OnDestroy {
  @ViewChild('alertsList') alertsList: CdkVirtualScrollViewport;
  @ViewChild('tmNotesList') tmNotesList: CdkVirtualScrollViewport;
  @ViewChild('content') content: IonContent;
  @ViewChild('notiSwiperRef') notiSwiperRef: any;

  private destroy$ = new Subject<void>();
  protected loadTmNotes$: Subscription;
  protected loadNotifications$: Subscription;
  protected loadSubmittedOrderSub$: Subscription;

  unreadAlertsCount$ = this.notificationService.getUnreadAlertsCount$();
  unreadNotesCount$ = this.notificationService.getUnreadNotesCount$();
  unreadAlerts$ = this.notificationService.getUnreadAlertsCount$();
  unreadNotes$ = this.notificationService.getUnreadNotesCount$();
  isUpcDeprecated$: Observable<boolean> = this.upcService.isUpcDeprecated();

  notifications: Notification[] = [];
  filteredNotifications: Notification[];
  tmNotes: Note[] = [];
  notesInView: Note[];
  filters: NotificationFilters;
  customers: Customer[];
  selectedCustomer: Customer;
  trackFilters: any;
  noteIds: string;

  stickyHeader: boolean;
  allRead: boolean;
  allReadTmNotes: boolean;
  hasFilters: boolean;
  isAlerts: boolean;
  isLoadingAlerts: boolean;
  isLoadingTmNotes: boolean;
  isNewCustomer: boolean;
  isSuperUser = false;
  showViewport = true;
  showFooter = false;

  totalTmNotes: number;
  scrollTimer: ReturnType<typeof setTimeout>;
  scrollTimerTmNotes: ReturnType<typeof setTimeout>;

  activeTab: string;

  private stickyHeaderTimer: ReturnType<typeof setTimeout>;
  private scrollEndTimer: ReturnType<typeof setTimeout>;
  private lastScrollTime = 0;
  private lastScrollPosition = 0;
  private lastContentHeight = 0;
  private readonly HEADER_HEIGHT = 191;
  private readonly MIN_SCROLL_INTERVAL = 100;
  private readonly SCROLL_THRESHOLD = 100;

  constructor(
    protected panAppState: PanAppState,
    protected notificationService: NotificationsService,
    readonly platformService: PlatformService,
    protected modalController: ModalController,
    private router: Router,
    private usabilla: UsabillaService,
    private platform: Platform,
    private customerStoreService: CustomerStoreService,
    public navigationHelper: NavigationHelperService,
    private modalService: ModalService,
    private orderService: OrderService,
    private upcService: UpcService,
    private actionSubject: ActionsSubject,
    private userService: UserService,
    private loginService: LoginService,
  ) {}

  ionViewWillEnter() {
    this.initializePage();
    this.setupSubscriptions();
    this.initializeFilters();
    this.initializeCustomerData();
    this.checkSuperUserStatus();
  }

  ionViewDidEnter() {
    if (this.isAlerts && this.filteredNotifications?.length > 0) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.markAsReadVisibleCards();
      }, environment.alertMarkAsReadTimeout);
    } else if (!this.isAlerts && this.notesInView?.length > 0) {
      clearTimeout(this.scrollTimerTmNotes);
      this.scrollTimerTmNotes = setTimeout(() => {
        this.markAsReadVisibleTmNotes();
      }, environment.alertMarkAsReadTimeout);
    }
  }

  ionViewWillLeave() {
    this.cleanup();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private initializePage(): void {
    this.panAppState.setPageTitle('Notifications');
    this.resetScrollPositions();
    this.initializeCounts();
    setTimeout(() => {
      this.loadInitialData();
    }, 0);
  }

  private resetScrollPositions(): void {
    if (this.alertsList) {
      this.alertsList?.scrollToIndex(0);
    }
    if (this.tmNotesList) {
      this.tmNotesList?.scrollToIndex(0);
    }
    if (this.content) {
      this.scrollToTop();
    }
  }

  private initializeCounts(): void {
    this.unreadAlertsCount$ = this.notificationService.getUnreadAlertsCount$();
    this.unreadNotesCount$ = this.notificationService.getUnreadNotesCount$();
  }

  private initializeFilters(): void {
    this.filters = this.notificationService?.loadFilters();
    const allEmpty = this.filters
      ? Object.keys(this.filters)?.every(key => this.filters[key]?.length === 0)
      : true;

    if (!this.filters || allEmpty) {
      this.clearFilters(true);
    } else {
      this.hasFilters = true;
      this.filterNotifications();
    }

    this.initializeTrackFilters();
  }

  private initializeTrackFilters(): void {
    this.trackFilters = {
      customerID: this.filters?.customers,
      alertType: this.filters?.alertTypes,
      alertStatus: this.filters?.statuses?.map(s => (s ? 'read' : 'unread')),
    };

    if (this.trackFilters) {
      this.trackFilters = Object.keys(this.trackFilters)
        ?.filter(k => this.trackFilters[k]?.length > 0)
        ?.reduce((a, k) => ({ ...a, [k]: this.trackFilters[k] }), {});
    }
  }

  private initializeCustomerData(): void {
    this.getTmNotes();
    this.setActiveTab();
    if (!this.isAlerts) {
      this.switchTab('alerts');
    } else {
      this.switchTab('tmnotes');
    }

    this.notificationService?.trackAlertsPageLoad(this.trackFilters);
  }

  private handleSubscriptionError(
    context: string,
  ): (error: any) => Observable<any> {
    return (error: any) => {
      console.error(`Error in ${context} subscription:`, error);
      return of([]);
    };
  }

  private updateReadStatus(): void {
    this.allRead =
      this.notifications?.filter(x => x?.read === true).length ===
      this.notifications?.length;
  }

  private updateNoteReadStatus(noteIds: string): void {
    const ids = noteIds?.split('|');
    for (let i = 0; i < ids?.length; i++) {
      for (let j = 0; j < this.tmNotes?.length; j++) {
        if (this.tmNotes[j]?.noteId?.toString() === ids[i]?.toString()) {
          this.tmNotes[j].read = true;
        }
      }
    }
    this.allReadTmNotes =
      this.tmNotes.filter(x => x?.read === true).length ===
      this.tmNotes?.length;
  }

  private handleCustomerChange(cust: Customer): void {
    if (
      cust &&
      cust?.customerNumber !== this.selectedCustomer?.customerNumber
    ) {
      this.isNewCustomer = true;
      this.selectedCustomer = cust;
      this.getTmNotes();
    }
  }

  private cleanup(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimers();
    this.scrollToTop();
  }

  private clearTimers(): void {
    clearTimeout(this.scrollTimer);
    clearTimeout(this.scrollTimerTmNotes);
    clearTimeout(this.stickyHeaderTimer);
    clearTimeout(this.scrollEndTimer);
  }

  private setupSubscriptions(): void {
    this.notificationService.getUnreadNotificationsCount();

    // Listen for unread counts updates
    this.actionSubject
      .pipe(
        ofType(ALERTS_ACTIONS.getUnreadAlertsCountSuccess),
        takeUntil(this.destroy$),
        catchError(this.handleSubscriptionError('unread alerts count')),
      )
      .subscribe(countData => {
        if (countData) {
          this.notificationService.setUnreadAlertsCount(
            countData?.alertCount?.alertCount,
          );
          this.notificationService.setUnreadNotesCount(
            countData?.alertCount?.noteCount,
          );
        }
      });

    // Listen for alert marking success
    this.actionSubject
      .pipe(
        ofType(ALERTS_ACTIONS.markAlertAsReadSuccess),
        takeUntil(this.destroy$),
        catchError(this.handleSubscriptionError('mark as read success')),
      )
      .subscribe(() => {
        this.updateReadStatus();
        this.notificationService?.getUnreadNotificationsCount();
      });

    // Listen for note marking success
    this.actionSubject
      .pipe(
        ofType(NOTES_ACTIONS.markNoteAsReadSuccess),
        takeUntil(this.destroy$),
        catchError(this.handleSubscriptionError('mark note as read success')),
      )
      .subscribe(noteData => {
        if (noteData) {
          this.updateNoteReadStatus(noteData?.response?.noteIds);
          this.notificationService?.getUnreadNotificationsCount();
        }
      });

    this.notificationService
      .getUnreadAlertsCount$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: count => {
          this.notificationService.setUnreadAlertsCount(count);
        },
        error: error => {
          console.error('Error getting unread notifications count:', error);
        },
      });

    // Listen for mark as read actions
    this.actionSubject
      .pipe(
        ofType(ALERTS_ACTIONS.markAlertAsRead, NOTES_ACTIONS.markNoteAsRead),
        takeUntil(this.destroy$),
        catchError(this.handleSubscriptionError('mark as read action')),
      )
      .subscribe(action => {
        if (action.type === ALERTS_ACTIONS.markAlertAsRead.type) {
          this.notificationService.markAsRead(action.payload.id);
        } else if (action.type === NOTES_ACTIONS.markNoteAsRead.type) {
          this.notificationService.markAsReadTmNote(action.payload.id);
        }
      });

    this.customerStoreService
      .loadCustomers$()
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading customers:', error);
          return of([]);
        }),
      )
      .subscribe(customers => {
        this.customers = customers;
      });

    this.customerStoreService
      .loadSelectedCustomer$()
      .pipe(
        skip(1),
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading selected customer:', error);
          return of(null);
        }),
      )
      .subscribe(cust => {
        this.handleCustomerChange(cust);
      });
  }

  scrollToTop() {
    this.content?.scrollToTop();
  }

  setActiveTab() {
    this.activeTab =
      this.navigationHelper.getQueryParams().get('tmnotes') === 'true' ||
      this.notificationService.getLastActiveTab() === 'tmnotes'
        ? 'tmnotes'
        : 'alerts';
    this.isAlerts = this.activeTab === 'alerts';
    this.notificationService.saveLastActiveTab(this.activeTab);
  }

  async openFilters() {
    if (this.notiSwiperRef?.nativeElement.swiper.activeIndex === 0) {
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        ModalWithNavComponent,
        {
          rootPage:
            this.platformService.platformType ===
            this.platformService.platformEnum.mobile
              ? NotificationFiltersMobileComponent
              : NotificationFiltersDesktopTabletComponent,
          rootPageParams: {
            customers: this.customers || [],
            filters: this.filters,
            platform: this.platformService.platformType,
          },
        },
        'notification-filters-modal-desktop',
      );
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss()?.then((resp: any) => {
        if (!Capacitor.isNativePlatform()) {
          this.usabilla.show();
        }
        if (resp?.data) {
          this.filters = resp.data.filters;
          let allEmpty = true;
          if (this.filters) {
            allEmpty = Object.keys(this.filters).every(key => {
              return this.filters[key]?.length === 0;
            });
          }
          if (!this.filters || allEmpty) {
            this.clearFilters(true);
          } else {
            this.hasFilters = true;
            this.handleFilters();
          }
        } else {
          this.clearFilters(true);
        }
      });
      await modal.present();
    }
  }

  clearFilters(firstLoad?: boolean) {
    this.hasFilters = false;
    this.filters = {
      customers: [],
      alertTypes: [],
      statuses: [],
    };

    if (this.alertsList) {
      setTimeout(() => {
        this.alertsList?.scrollToIndex(0);
        this.handleFilters(firstLoad);
      }, 0);
    } else {
      this.handleFilters(firstLoad);
    }
  }

  handleFilters(firstLoad?: boolean) {
    let trackFilters: any = {
      customerID: this.filters?.customers,
      alertType: this.filters?.alertTypes,
      alertStatus: this.filters?.statuses?.map(s => (s ? 'read' : 'unread')),
    };
    if (trackFilters) {
      trackFilters = Object.keys(trackFilters)
        .filter(k => trackFilters[k]?.length > 0)
        .reduce((a, k) => ({ ...a, [k]: trackFilters[k] }), {});
    }
    if (!firstLoad)
      this.notificationService.trackAlertFiltersApplied(trackFilters);
    this.notificationService.saveFilters(this.filters);
    this.filterNotifications();
  }

  getTmNotes() {
    if (this.loadTmNotes$) {
      this.loadTmNotes$.unsubscribe();
    }

    this.showViewport = false;
    this.isLoadingTmNotes = true;

    this.loadTmNotes$ = this.notificationService
      .loadTmNotes()
      ?.pipe(
        debounceTime(100),
        filter(n => !!n && !n.loading && n.loaded),
        take(1),
        takeUntil(this.destroy$),
      )
      ?.subscribe({
        next: n => {
          if (n?.notes?.length > 0) {
            this.tmNotes = n.notes;
            this.setVisibleNotes();

            if (this.isNewCustomer) {
              this.noteIds = this.tmNotes.map(n => 'TMN-' + n.noteId).join(',');
              this.trackTmNotesPageLoad();
              this.isNewCustomer = false;
            }
          } else {
            this.tmNotes = null;
            this.notesInView = null;
            this.noteIds = null;
          }
          this.isLoadingTmNotes = false;

          if (this.notesInView && !this.allReadTmNotes) {
            clearTimeout(this.scrollTimerTmNotes);
            this.scrollTimerTmNotes = setTimeout(() => {
              this.markAsReadVisibleTmNotes();
            }, environment.alertMarkAsReadTimeout);
          }

          setTimeout(() => {
            this.showViewport = true;
          }, 0);
        },
        error: err => {
          console.error('Error loading TM notes:', err);
          this.isLoadingTmNotes = false;
          this.showViewport = true;
        },
      });
  }

  filterNotifications() {
    if (this.loadNotifications$) {
      this.loadNotifications$.unsubscribe();
    }
    this.notifications = [];
    this.filteredNotifications = [];
    this.showViewport = false;

    this.loadNotifications$ = this.notificationService
      .loadNotifications()
      ?.pipe(debounceTime(100), takeUntil(this.destroy$))
      ?.subscribe(x => {
        this.isLoadingAlerts = x.loading;
        if (!this.isLoadingAlerts) {
          let newNotifications = [...x.notifications];
          if (this.filters?.customers?.length) {
            newNotifications = newNotifications.filter(notification =>
              this.filters.customers.includes(
                notification.customerNumber.toString(),
              ),
            );
          }

          if (this.filters?.alertTypes?.length) {
            newNotifications = newNotifications.filter(notification =>
              this.filters.alertTypes.some(
                type =>
                  type.toUpperCase() === notification.alertType.toUpperCase(),
              ),
            );
          }

          if (this.filters?.statuses?.length) {
            newNotifications = newNotifications.filter(notification =>
              this.filters.statuses.some(status =>
                status ? notification.read === status : !notification.read,
              ),
            );
          }

          this.notifications = [...newNotifications];
          this.filteredNotifications = [...newNotifications];
          this.allRead = newNotifications.every(
            notification => notification.read,
          );

          if (this.filteredNotifications && !this.allRead) {
            clearTimeout(this.scrollTimer);
            this.scrollTimer = setTimeout(() => {
              this.markAsReadVisibleCards();
            }, environment.alertMarkAsReadTimeout);
          }

          setTimeout(() => {
            this.showViewport = true;
            setTimeout(() => this.checkFooterVisibility(), 0);
          }, 0);
        } else {
          this.checkFooterVisibility();
        }
      });
  }

  markAsRead(unreadNotificationsIds: string): void {
    if (unreadNotificationsIds !== '') {
      this.notificationService.markAsRead(unreadNotificationsIds);
    }
  }

  markAsReadTmNote(unreadTmNotesIds: string) {
    if (unreadTmNotesIds !== '') {
      this.notificationService.markAsReadTmNote(unreadTmNotesIds);
    }
  }

  markAllAsRead(): void {
    if (this.notiSwiperRef?.nativeElement.swiper.activeIndex === 0) {
      this.showViewport = false;
      this.notificationService.markAsRead('all');
      this.filteredNotifications = this.filteredNotifications?.map(fn =>
        !fn?.read ? { ...fn, read: true } : fn,
      );
      this.notifications = this.notifications?.map(n =>
        !n?.read ? { ...n, read: true } : n,
      );
      this.allRead = true;
      setTimeout(() => {
        this.showViewport = true;
      }, 0);
    } else {
      this.showViewport = false;
      this.notificationService.markAsReadTmNote('all');
      this.notesInView = this.notesInView?.map(n =>
        !n?.read ? { ...n, read: true } : n,
      );
      this.tmNotes = this.tmNotes?.map(n =>
        !n?.read ? { ...n, read: true } : n,
      );
      this.allReadTmNotes = true;
      setTimeout(() => {
        this.showViewport = true;
      }, 0);
    }
  }

  doCTA(currentNotification: Notification) {
    if (
      currentNotification.alertId === AlertTypeEnum.DELIVERY_EXCEPTION ||
      currentNotification.alertId === AlertTypeEnum.ORDERED_PRODUCT_UNAVAILABLE
    ) {
      //Delivery Issues
      this.navigationHelper.routeToNotificationDetails(
        currentNotification.alertBatchId,
      );
    } else if (currentNotification.alertId === AlertTypeEnum.DELIVERY_HEADSUP) {
      //Delivery Heads Up
      const orderNumber = currentNotification.messageBody.split(' ')[1];
      const params = AlertIdData.find(
        a => a.alertId === currentNotification.alertId,
      )?.url.split('?')[1];
      this.loadSubmittedOrderSub$ = this.orderService
        ?.selectOrderWithTandemNumber(Number(orderNumber))
        ?.subscribe(order => {
          if (order) {
            const orderInfo =
              `${order.orderHeader.customerNumber}:` +
              `${order.orderHeader.departmentNumber}:` +
              `${order.orderHeader.orderId}` +
              `?${params}`;
            this.navigateTo(['order', 'submitted-order', orderInfo]);
          } else {
            this.navigateTo(['my-orders?' + params]);
          }
        });
    } else if (
      currentNotification.alertId === AlertTypeEnum.DIRECT_SHIP_TRACKING ||
      currentNotification.alertId === AlertTypeEnum.DIRECT_STANDARD_DELIVERY ||
      currentNotification.alertId === AlertTypeEnum.DIRECT_ORDER_EXCEPTIONS ||
      currentNotification.alertId === AlertTypeEnum.DIRECT_DELIVERY_EXCEPTIONS
    ) {
      const orderNumber = currentNotification?.alertContext?.find(
        a => a.key === 'orderNumber',
      )?.value;
      this.loadSubmittedOrderSub$ = this.orderService
        ?.selectOrderWithTandemNumber(Number(orderNumber))
        ?.subscribe(async order => {
          try {
            if (order) {
              const orderInfo =
                `${order.orderHeader.customerNumber}:` +
                `${order.orderHeader.departmentNumber}:` +
                `${order.orderHeader.orderId}`;

              await this.loginService.switchToCustomer(
                order?.orderHeader?.customerNumber,
                order?.orderHeader?.departmentNumber,
                order?.orderHeader?.divisionNumber,
              );
              this.navigateTo(['order', 'submitted-order', orderInfo]);
            } else {
              await this.loginService.switchToCustomer(
                currentNotification?.customerNumber,
                currentNotification?.departmentNumber ?? 0,
                currentNotification?.divisionNumber,
              );
              this.navigateTo(['my-orders-view']);
            }
          } catch (error) {
            this.navigateTo(['my-orders-view']);
          }
        });
    } else {
      let url = AlertIdData.find(
        a => a.alertId === currentNotification.alertId,
      )?.url;
      if (currentNotification.alertId === AlertTypeEnum.PYMT_DUE_TODAY) {
        url += '&alertName=PYMT_DUE_TODAY';
      } else if (
        currentNotification.alertId === AlertTypeEnum.PYMT_DUE_THIS_WK
      ) {
        url += '&alertName=PYMT_DUE_THIS_WK';
      } else if (currentNotification.alertId === AlertTypeEnum.PYMT_PAST_DUE) {
        url += '&alertName=PYMT_PAST_DUE';
      }
      const isPaymentNotification =
        currentNotification.alertId === AlertTypeEnum.PYMT_DUE_TODAY ||
        currentNotification.alertId === AlertTypeEnum.PYMT_DUE_THIS_WK ||
        currentNotification.alertId === AlertTypeEnum.PYMT_PAST_DUE;
      if (isPaymentNotification) {
        this.userService
          .showInvoiceAndPaymentsMenu$()
          .pipe(take(1))
          .subscribe(menuLabel => {
            if (!menuLabel) {
              url = '';
            } else if (menuLabel === 'i18n.moxe-links.invoices') {
              url = url.replace('/make-payment', '');
            }
            if (url) {
              this.navigateTo([url]);
            }
          });
      } else {
        if (url) {
          this.navigateTo([url]);
        }
      }
    }
  }

  navigateTo(path: string[], params?: any) {
    const deviceTypePath = this.platformService?.isTouch?.value
      ? path.unshift('touch')
      : path.unshift('desktop');
    if (params) {
      this.router.navigate(path, params);
    } else {
      if (path.length === 2)
        this.router.navigateByUrl('/' + path[0] + '/' + path[1]);
      else if (path.length === 4)
        this.router.navigateByUrl(
          '/' + path[0] + '/' + path[1] + '/' + path[2] + '/' + path[3],
        );
    }
  }

  async markAsReadVisibleCards() {
    clearTimeout(this.scrollTimer);
    if (this.filteredNotifications && this.alertsList && this.isAlerts) {
      const viewportElement = this.alertsList?.elementRef?.nativeElement;
      const cards = Array.from(viewportElement?.querySelectorAll('ion-item'));
      let firstCardIndex: number;
      let lastCardIndex: number;
      firstCardIndex = -1;

      cards.forEach((card: HTMLElement, index) => {
        if (this.isElementInViewport(card)) {
          if (firstCardIndex === -1) {
            firstCardIndex = index;
          }
          lastCardIndex = index + 1;
        }
      });

      if (firstCardIndex > -1) {
        const visibleCards = this.filteredNotifications?.slice(
          firstCardIndex,
          lastCardIndex,
        );
        let unreadVisibleCardsIds = '';
        for (const visibleCard of visibleCards) {
          if (!visibleCard.read) {
            unreadVisibleCardsIds += `|${visibleCard.alertBatchId}`;
          }
        }
        unreadVisibleCardsIds = unreadVisibleCardsIds.slice(1);
        this.markAsRead(unreadVisibleCardsIds);
      }
    }
  }

  markAsReadVisibleTmNotes() {
    if (this.notesInView && this.tmNotesList && !this.isAlerts) {
      const viewportElement = this.tmNotesList.elementRef.nativeElement;
      const notes = Array.from(viewportElement.querySelectorAll('ion-item'));
      let firstNoteIndex: number;
      let lastNoteIndex: number;
      firstNoteIndex = -1;

      notes.forEach((note: HTMLElement, index) => {
        if (this.isElementInViewport(note)) {
          if (firstNoteIndex === -1) {
            firstNoteIndex = index;
          }
          lastNoteIndex = index + 1;
        }
      });

      if (firstNoteIndex > -1) {
        const visibleNotes = this.notesInView?.slice(
          firstNoteIndex,
          lastNoteIndex,
        );
        let unreadVisibleNotesIds = '';
        visibleNotes.forEach(note => {
          if (!note.read) {
            unreadVisibleNotesIds += `|${note.noteId}`;
          }
        });
        unreadVisibleNotesIds = unreadVisibleNotesIds.slice(1);
        this.markAsReadTmNote(unreadVisibleNotesIds);
      }
    }
  }

  isElementInViewport(el: HTMLElement) {
    const rect = el?.getBoundingClientRect();
    const top =
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop
        ? this.HEADER_HEIGHT
        : 0;
    return (
      rect.top >= top &&
      rect.left >= 0 &&
      rect.bottom <= this.platform?.height() &&
      rect.right <= this.platform?.width()
    );
  }

  switchTab(event: any) {
    if (event === 'alerts') {
      this.notiSwiperRef?.nativeElement.swiper.slideTo(1);
      this.navigationHelper.routeTo(PATHS.NOTIFICATIONS, {
        tmnotes: true,
      });
      this.activeTab = 'tmnotes';
      this.isAlerts = false;

      // Reset scroll position with a small delay to ensure the view is rendered
      setTimeout(() => {
        if (this.tmNotesList) {
          this.tmNotesList.scrollToIndex(0);
          this.stickyHeader = false;
        }
        this.scrollToTop();
      }, 50);

      if (this.tmNotes) {
        this.noteIds = this.tmNotes.map(n => 'TMN-' + n.noteId).join(',');
        this.trackTmNotesPageLoad();
      }
      clearTimeout(this.scrollTimerTmNotes);
      this.scrollTimerTmNotes = setTimeout(() => {
        this.markAsReadVisibleTmNotes();
      }, environment.alertMarkAsReadTimeout);
    } else {
      this.notiSwiperRef?.nativeElement.swiper.slideTo(0);
      this.navigationHelper.routeTo(PATHS.NOTIFICATIONS);
      this.activeTab = 'alerts';
      this.isAlerts = true;

      // Reset scroll position with a small delay to ensure the view is rendered
      setTimeout(() => {
        if (this.alertsList) {
          this.alertsList.scrollToIndex(0);
          this.stickyHeader = false;
        }
        this.scrollToTop();
      }, 50);

      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.markAsReadVisibleCards();
      }, environment.alertMarkAsReadTimeout);
      this.notificationService.trackAlertsPageLoad(this.trackFilters);
    }
    this.notificationService.saveLastActiveTab(this.activeTab);
  }

  setVisibleNotes() {
    this.notesInView = this.tmNotes;
    this.allReadTmNotes =
      this.tmNotes.filter(x => x.read === true).length === this.tmNotes.length;
    if (this.notesInView && !this.allReadTmNotes) {
      clearTimeout(this.scrollTimerTmNotes);
    }
    setTimeout(() => this.checkFooterVisibility(), 0);
  }

  openUPC() {
    this.upcService.openUPC();
  }

  openManageAlerts() {
    this.router.navigate([PATHS.NOTIFICATIONS, PATHS.NOTIFICATION_PREFERENCES]);
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  trackTmNotesPageLoad() {
    const prodTrackingInfo: any[] = [];
    this.tmNotes?.forEach(note => {
      if (note?.products && note?.products?.length > 0) {
        note?.products.forEach(np => {
          const p = np as Product;
          prodTrackingInfo.push({
            divisionAPN: p?.summary?.divisionNumber + '-' + p?.productNumber,
            attributes: p?.trackingAttributes,
            merchFeatureName: 'tm notes',
            merchFeatureView: '1',
          });
        });
      }
    });
    this.notificationService.trackTmNotesPageLoad(
      this.noteIds,
      prodTrackingInfo,
    );
  }

  onCdkScroll(event: any) {
    const now = Date.now();
    if (now - this.lastScrollTime < this.MIN_SCROLL_INTERVAL) {
      return;
    }
    this.lastScrollTime = now;

    const target = event?.target;
    const scrollOffset = target?.scrollTop;
    const scrollDirection =
      scrollOffset > this.lastScrollPosition ? 'down' : 'up';
    this.lastScrollPosition = scrollOffset;
    const isAtTop = scrollOffset <= 0;
    const isAboveHeader = scrollOffset < this.HEADER_HEIGHT;

    // Check footer visibility immediately
    this.checkFooterVisibility();

    // Update sticky header state
    clearTimeout(this.stickyHeaderTimer);

    // Handle immediate cases first
    if (
      isAtTop ||
      (scrollDirection === 'up' && scrollOffset <= this.HEADER_HEIGHT)
    ) {
      this.stickyHeader = false;
    } else if (
      scrollDirection === 'down' &&
      scrollOffset > this.HEADER_HEIGHT + 100
    ) {
      // Add a small delay to let scroll position stabilize
      this.stickyHeaderTimer = setTimeout(() => {
        // Recheck scroll position after delay
        const currentScroll = target?.scrollTop;
        if (currentScroll > this.HEADER_HEIGHT + 100) {
          this.stickyHeader = true;
        }
      }, 100);
    } else {
      // Debounce for intermediate positions
      this.stickyHeaderTimer = setTimeout(() => {
        const currentScroll = target?.scrollTop;
        if (
          isAboveHeader ||
          (scrollDirection === 'up' &&
            currentScroll <= this.HEADER_HEIGHT - 100)
        ) {
          this.stickyHeader = false;
        } else if (
          scrollDirection === 'down' &&
          currentScroll > this.HEADER_HEIGHT + 100
        ) {
          this.stickyHeader = true;
        }
      }, 50);
    }

    // Add a check when scroll momentum ends
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      this.checkFooterVisibility();
    }, 150);

    if (this.isAlerts) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.markAsReadVisibleCards();
      }, environment.alertMarkAsReadTimeout);
    } else {
      clearTimeout(this.scrollTimerTmNotes);
      this.scrollTimerTmNotes = setTimeout(() => {
        this.markAsReadVisibleTmNotes();
      }, environment.alertMarkAsReadTimeout);
    }
  }

  handleScroll(event: any) {
    if (this.isAlerts) {
      this.onCdkScroll(event);
    } else {
      this.onCdkScroll(event);
    }
  }

  trackByFn(index: number, item: any): string {
    return item.alertBatchId || item.noteId || index.toString();
  }

  private checkSuperUserStatus() {
    combineLatest([
      this.panAppState?.feature$([
        FEATURES.split_global_alerts_super_user.name,
      ]),
      this.userService?.isSuperUser$(),
    ])
      ?.pipe(
        map(([flagEnabled, isSuperUser]) => {
          return {
            flagEnabled,
            isSuperUser,
          };
        }),
        take(1),
      )
      ?.subscribe(res => {
        if (res?.flagEnabled) {
          this.isSuperUser = res?.isSuperUser;
        }
      });
  }

  private loadInitialData(): void {
    if (this.isAlerts) {
      this.loadNotifications();
    } else {
      this.getTmNotes();
    }
  }

  private loadNotifications(): void {
    this.notificationService.loadNotifications()?.subscribe();
  }

  private checkFooterVisibility() {
    const viewport = this.isAlerts ? this.alertsList : this.tmNotesList;
    if (!viewport) return;

    const viewportElement = viewport.elementRef.nativeElement;
    const contentHeight = viewportElement.scrollHeight;
    const viewportHeight = viewportElement.clientHeight;
    const currentScrollPosition = viewportElement.scrollTop;

    // Check if we're at the bottom or if there's no content
    const isAtBottom =
      contentHeight - (currentScrollPosition + viewportHeight) <=
      this.SCROLL_THRESHOLD;
    const hasNoContent =
      (!this.isAlerts && (!this.tmNotes || this.tmNotes.length === 0)) ||
      (this.isAlerts &&
        (!this.notifications || this.notifications.length === 0));

    // Show footer if we're at the bottom OR if there's no content
    this.showFooter =
      (isAtBottom || hasNoContent) &&
      this.platformService.platformType ===
        this.platformService.platformEnum.desktop;

    // If content height changed, we might need to recheck
    if (contentHeight !== this.lastContentHeight) {
      this.lastContentHeight = contentHeight;
      setTimeout(() => this.checkFooterVisibility(), 0);
    }
  }
}
